import Link from "next/link";
import { FaTiktok } from "react-icons/fa";
import {
	FaFacebook,
	FaInstagram,
	FaLinkedinIn,
	FaXTwitter,
	FaYoutube,
} from "react-icons/fa6";

import FooterCategory from "./FooterCategory";
import { useLazyIntercom } from "~/hooks";

const footerContent = {
	education: [
		{ name: `Courses`, href: `/courses` },
		{ name: `Articles`, href: `/articles` },
		{ name: `Videos`, href: `/videos` },
		{ name: `Workshops`, href: `/live-events/workshops` },
		{ name: `Webinars`, href: `/webinars` },
	],
	company: [
		{ name: `About`, href: `/about` },
		{ name: `Partners`, href: `/about#partners` },
		{ name: `Accreditations`, href: `/about#accreditations` },
		{
			name: "Help Center",
			href: "https://help.brookbushinstitute.com",
		},
	],
	additional: [
		{ name: `Referral Program`, href: `/referrals/program` },
		{ name: `Team Membership`, href: `/team-membership` },
		{ name: `Brookbush AI`, href: `/chat` },
		{ name: `Program Generator`, href: `/info/programs` },
	],
	certifications: [
		{
			name: `Certified Personal Trainer (CPT) Programs`,
			href: `/info/certified-personal-trainer`,
		},
		{
			name: `Human Movement Specialist (HMS) Certification`,
			href: `/info/human-movement-specialist`,
		},
		{
			name: `Integrated Manual Therapist (IMT) Certification`,
			href: `/info/integrated-manual-therapist`,
		},
		{
			name: `Strength and Performance Coach (SPC) Certification`,
			href: `/info/strength-and-performance-coach`,
		},
	],

	certificationsByProfessions: [
		{
			name: `Certified Athletic Trainers`,
			href: `/info/certified-athletic-trainers`,
		},
		{ name: `Athletic Therapists (Canada)`, href: `/info/athletic-therapists` },
		{
			name: `Certified Personal Trainers`,
			href: `/info/personal-trainer-continuing-education`,
		},
		{ name: `Chiropractors (DC)`, href: `/info/chiropractors` },
		{
			name: `Licensed Massage Therapists (LMTs)`,
			href: `/info/licensed-massage-therapists`,
		},
		{ name: `Occupational Therapists`, href: `/info/occupational-therapists` },
		{
			name: `Physical Therapists and Physical Therapy Assistants`,
			href: `/info/PT-PTA-CE`,
		},
		{
			name: `Physiotherapist and Physiotherapist Assistant`,
			href: `/info/physio`,
		},
		{
			name: `Registered Massage Therapist`,
			href: `/info/registered-massage-therapists`,
		},
	],
	courses: [
		{
			name: `BI-CPT`,
			href: `/certifications/certified-personal-trainer`,
		},
		{
			name: `HMS`,
			href: `/certifications/human-movement-specialist`,
		},
		{
			name: `IMT`,
			href: `/certifications/integrated-manual-therapist`,
		},
		{
			name: `SPC`,
			href: `/certifications/strength-and-performance-coach`,
		},
	],
	social: [
		{
			name: `Facebook`,
			href: `https://www.facebook.com/profile.php?id=61579303564825`,
			icon: FaFacebook,
		},
		{
			name: `Instagram`,
			href: `https://www.instagram.com/brookbushinstitute`,
			icon: FaInstagram,
		},
		{
			name: `X`,
			href: `https://x.com/BrookbushInst`,
			icon: FaXTwitter,
		},
		{
			name: `LinkedIn`,
			href: `https://www.linkedin.com/company/brookbush-institute`,
			icon: FaLinkedinIn,
		},
		{
			name: `Youtube`,
			href: `https://www.youtube.com/@BrookbushInstitute`,
			icon: FaYoutube,
		},
		{
			name: `TikTok`,
			href: `https://www.tiktok.com/@brookbushinstitute`,
			icon: FaTiktok,
		},
	],
};

const Footer = () => {
	const { preload, show } = useLazyIntercom();
	return (
		<footer className="relative z-10 border-t bg-slate-50 p-4 sm:p-6 lg:p-12 dark:border-slate-600 dark:bg-slate-700">
			<section className="my-4 flex flex-col items-center sm:my-6 lg:flex-row lg:items-start lg:justify-around">
				<section className="flex w-full max-w-5xl flex-col gap-4 sm:gap-6">
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
						<FooterCategory
							category={footerContent.education}
							title="Education"
						/>
						<FooterCategory
							category={footerContent.additional}
							title="Additional Features"
						/>
						<FooterCategory
							category={footerContent.company}
							title="Company"
							className="sm:col-span-2 md:col-span-1"
						/>
					</div>

					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
						<FooterCategory
							category={footerContent.certificationsByProfessions}
							title="Continuing Education by Profession"
						/>
						<div className="grid grid-cols-1 gap-6">
							<FooterCategory
								category={footerContent.certifications}
								title="Certifications"
							/>
							<FooterCategory
								category={footerContent.courses}
								title="Courses"
							/>
						</div>
					</div>
				</section>

				<article className="mt-8 w-full py-6 text-center lg:mt-0 lg:ml-8 lg:w-auto lg:py-0 lg:text-left">
					<h4 className="mb-2 font-medium text-gray-900 text-xl sm:text-2xl dark:text-gray-50">
						Are you looking for additional help?
					</h4>
					<p className="mb-5 text-sm sm:text-base">
						Our team is here to help you find the right answer for your
						question.
					</p>
					<button
						onMouseEnter={preload}
						onFocus={preload}
						onClick={() => show()}
						className="inline-block rounded-full bg-sky-500 px-4 py-2 font-medium text-base text-white shadow-lg hover:brightness-95 sm:text-lg dark:bg-sky-600"
					>
						Contact Support
					</button>
				</article>
			</section>

			<div className="phablet:mt-10 mb-8 phablet:mb-0 flex flex-col-reverse items-center gap-4 md:flex-row md:justify-between">
				<div className="flex justify-center space-x-4 sm:space-x-6 md:order-2">
					{footerContent.social.map((item) => (
						<Link
							prefetch={false}
							key={item.name}
							href={item.href}
							className="text-gray-400 hover:text-gray-500"
						>
							<span className="sr-only">{item.name}</span>
							<item.icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
						</Link>
					))}
				</div>

				<div className="text-center md:text-left">
					<p className="text-gray-400 text-sm sm:text-base md:order-1">
						&copy; {new Date().getFullYear()} Brookbush Institute, Inc. All
						rights reserved.
					</p>

					<div className="mt-1.5 flex justify-center gap-4 sm:gap-5 md:justify-start">
						<Link
							prefetch={false}
							href="/privacy-policy"
							className="text-gray-400 text-xs sm:text-sm md:order-1"
						>
							Privacy Policy
						</Link>
						<Link
							prefetch={false}
							href="/terms-of-service"
							className="text-gray-400 text-xs sm:text-sm md:order-1"
						>
							Terms Of Service
						</Link>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
